import { APIv4 as API } from 'src/libs/axios';
import { alertError } from 'src/libs/toastify';
import { EnumItem } from '../types';
import { formatDate } from 'src/libs/moment';

const Messages = {
  FETCH_INCOMES: 'Ошибка получения списка приемок',
  FETCH_INCOME: 'Ошибка получения данных приемки',
  CREATE_INCOME: 'Ошибка создания приемки',
  FETCH_ENUMS: 'Ошибка получения справочных данных',
};

export type WarehouseIncomeType = 0 | 1 | 2 | 3 | 4 | 5;
export type WarehouseIncomeStatus = 0 | 5 | 10 | 15;

export type WarehouseIncomeNomenclatureRef = {
  id: string;
  name: string;
};

export type WarehouseIncomeListItem = {
  id: string;
  type: WarehouseIncomeType;
  number: string;
  timeStamp: string;
  warehouse: WarehouseIncomeNomenclatureRef | null;
  supplier: WarehouseIncomeNomenclatureRef | null;
  status: WarehouseIncomeStatus;
  comment: string | null;
  createdAt: string;
};

export type WarehouseIncomeSearchParams = {
  supplierId?: string;
  warehouseId?: string;
  type?: WarehouseIncomeType;
  status?: WarehouseIncomeStatus;
  dateFrom?: string;
  dateTo?: string;
};

export type WarehouseIncomeItemPayload = {
  nomenclatureId: string;
  quantity: number;
  purchasePrice: number;
  batchNumber: string;
  comment: string;
};

export type WarehouseIncomePayload = {
  warehouseId: string;
  documentDateTime: string;
  type: WarehouseIncomeType;
  comment: string;
  supplierId: string | null;
  items: WarehouseIncomeItemPayload[];
};

export type WarehouseIncomeItemValues = {
  id?: string;
  nomenclatureId: string | null;
  nomenclatureName?: string;
  nomenclatureUnitOfMeasurement?: number;
  quantity: number | null;
  purchasePrice: number | null;
  batchNumber: string;
  comment: string;
};

export type WarehouseIncomeValues = {
  warehouseId: string | null;
  documentDateTime: Date | null;
  type: WarehouseIncomeType | null;
  comment: string;
  supplierId: string | null;
  items: WarehouseIncomeItemValues[];
};

export type WarehouseIncomeItemNomenclature = {
  id: string;
  name: string;
  unitOfMeasurement: number;
};

export type WarehouseIncomeItemDetail = {
  id: string;
  nomenclature: WarehouseIncomeItemNomenclature | null;
  quantity: number;
  purchasePrice: number;
  batchNumber: string | null;
  comment: string | null;
};

export type WarehouseIncomeDetail = {
  id: string;
  type: WarehouseIncomeType;
  number: string;
  timeStamp: string;
  warehouseId: string;
  shiftId: string | null;
  supplierId: string | null;
  inventoryId: string | null;
  transferWarehouseDocumentId: string | null;
  productionWarehouseDocumentId: string | null;
  supplyDocumentId: string | null;
  receiptSupplyDocumentId: string | null;
  status: WarehouseIncomeStatus;
  comment: string | null;
  createdAt: string;
  items: WarehouseIncomeItemDetail[];
};

const adaptWarehouseIncome = (
  values: WarehouseIncomeValues
): WarehouseIncomePayload => ({
  warehouseId: values.warehouseId ?? '',
  documentDateTime: formatDate(
    values.documentDateTime,
    'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
  ),
  type: values.type ?? 0,
  comment: values.comment ?? '',
  supplierId: values.supplierId ?? '',
  items: values.items.map((item) => ({
    nomenclatureId: item.nomenclatureId ?? '',
    quantity: item.quantity ?? 0,
    purchasePrice: item.purchasePrice ?? 0,
    batchNumber: item.batchNumber ?? '',
    comment: item.comment ?? '',
  })),
});

const BASE_URL = '/back-office/warehouse-documents/incomes';
const ENUMS_URL = '/back-office/enums';

export const fetchAllWarehouseIncomes = (
  params?: WarehouseIncomeSearchParams
): Promise<WarehouseIncomeListItem[] | void> =>
  API.get<WarehouseIncomeListItem[]>(BASE_URL, { params })
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_INCOMES));

export const fetchWarehouseIncome = ({
  id,
}: {
  id: string;
}): Promise<WarehouseIncomeDetail | void> =>
  API.get<WarehouseIncomeDetail>(`${BASE_URL}/${id}`).then(({ data }) => data);

export const addWarehouseIncome = (
  data: WarehouseIncomeValues
): Promise<string> =>
  API.post<string>(BASE_URL, adaptWarehouseIncome(data)).then(
    ({ data }) => data
  );

export const fetchIncomeDocumentTypes = (): Promise<EnumItem[] | void> =>
  API.get<EnumItem[]>(`${ENUMS_URL}/income-warehouse-document-types`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));

export const fetchWarehouseDocumentStatuses = (): Promise<EnumItem[] | void> =>
  API.get<EnumItem[]>(`${ENUMS_URL}/warehouse-document-statuses`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));
